import { CrewUi, isNonNullable, isTruthy } from 'corso-types';
import Disclosure from '~/components/Disclosure';
import Divide from '~/components/Divide';
import Icon from '~/components/Icon';
import Link from '~/components/Link';
import { CardOption } from '~/components/form-controls/RadioGroup';
import { FIELD_KEY } from './returnMethods';

type HappyReturnsMethod = Extract<
  CrewUi.ShipmentMethod,
  {
    provider: 'Happy_Returns';
  }
>;

type HappyReturnLocation = HappyReturnsMethod['dropoffLocations'][number];
const PRIVACY_POLICY_URL = 'https://www.privacypolicy.happyreturns.com/en-us';
const ALL_LOCATIONS_URL = 'https://locations.happyreturns.com';
const MARK_URL = 'https://cdn.corso.com/img/hr-circle-logo.png';

// HR numbers openingHours 1 = Monday ... 7 = Sunday, so this doubles as the week's display order
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatTime = (iso: string) => {
  const date = new Date(iso);

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    // whole hours read better as "9 AM" than "9:00 AM"
    minute: date.getMinutes() === 0 ? undefined : '2-digit',
  });
};

// Collapse consecutive days sharing the same hours into "Mon - Fri: 8:30 AM - 6:30 PM" rows
const toWeeklyHours = (location: HappyReturnLocation) => {
  const groups: { hours: string; from: string; to: string }[] = [];

  for (const [index, day] of DAY_LABELS.entries()) {
    const entry = location.openingHours.find((h) => h.dayOfWeek === index + 1);
    const hours =
      entry?.openingTime && entry.closingTime
        ? `${formatTime(entry.openingTime)} - ${formatTime(entry.closingTime)}`
        : 'CLOSED';
    const last = groups.at(-1);

    if (last && last.hours === hours) last.to = day;
    else groups.push({ hours, from: day, to: day });
  }

  return groups.map(({ hours, from, to }) => ({
    hours,
    days: from === to ? from : `${from} - ${to}`,
  }));
};

const todayHoursText = (location: HappyReturnLocation) => {
  // getDay() is 0 = Sunday, which HR numbers as 7
  const today = new Date().getDay() || 7;
  const entry = location.openingHours.find((h) => h.dayOfWeek === today);

  return entry?.closingTime
    ? `Open today until ${formatTime(entry.closingTime)}`
    : 'Closed today';
};

const formatAddress = ({ address }: HappyReturnLocation) =>
  [address.address, address.city, address.state, address.zipcode]
    .filter(isTruthy)
    .join(', ');

export function HappyReturnsReturnMethod({
  method,
  defaultChecked,
}: {
  method: HappyReturnsMethod;
  defaultChecked: boolean;
}) {
  const { dropoffLocations } = method;
  return (
    <CardOption
      value={JSON.stringify(method)}
      name={FIELD_KEY}
      defaultChecked={defaultChecked}
      aria-labelledby="return-method-happy-returns-label"
      aria-describedby="return-method-happy-returns-description"
      required
    >
      <div className="flex gap-3">
        <img
          aria-hidden
          alt="Happy Returns"
          className="size-10 shrink-0"
          src={MARK_URL}
        />
        <div className="flex flex-col gap-1">
          <span
            id="return-method-happy-returns-label"
            className="subheading-sm text-on-surface"
          >
            Drop off at any Happy Returns® location
          </span>
          <span
            id="return-method-happy-returns-description"
            className="body-md text-on-surface-variant"
          >
            Packaging &amp; label-free.
          </span>
        </div>
      </div>

      <p className="mt-3 body-sm text-on-surface-variant">
        By continuing, you acknowledge you have had an opportunity to read{' '}
        <Link
          to={PRIVACY_POLICY_URL}
          reloadDocument
          target="_blank"
          rel="noreferrer"
          className="text-on-surface underline underline-offset-2"
        >
          Happy Returns Privacy Policy
        </Link>
        .
      </p>

      <Divide
        as="ul"
        ariaLabel="Nearby Happy Returns locations"
        // Divide collapses the first row's top padding, but it needs to clear the border
        className="mt-4 border-t border-outline-variant *:first:pt-4"
      >
        {dropoffLocations.map((location) => (
          <li
            // happy returns doesn't always send a location id, name + address is unique without it
            key={
              location.idFromPlatform ??
              `${location.name}-${formatAddress(location)}`
            }
            className="flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-4">
              <span className="label-md text-on-surface">{location.name}</span>
              {isNonNullable(location.distance) && (
                <span className="shrink-0 body-sm text-on-surface-variant">
                  {location.distance.toFixed(1)} mi
                </span>
              )}
            </div>

            <Disclosure
              hideIndicator
              // the card surface mixes on hover, an opaque disclosure would sit on top of it as a white block
              className="bg-transparent"
              summary={
                <div className="flex items-center gap-2 body-sm text-on-surface-variant">
                  <span>{todayHoursText(location)}</span>
                  <span className="inline-flex cursor-pointer items-center gap-1 text-on-surface underline underline-offset-2">
                    <span className="group-open/details:hidden">show more</span>
                    <span className="hidden group-open/details:inline">
                      show less
                    </span>
                    <Icon
                      icon="ChevronDown"
                      className="size-3 transition-transform group-open/details:rotate-180"
                    />
                  </span>
                </div>
              }
            >
              <div className="mt-2 flex flex-col gap-3 body-sm text-on-surface-variant">
                <div className="flex gap-2">
                  <Icon icon="Clock" className="mt-0.5 size-4 shrink-0" />
                  <div className="flex flex-col">
                    {toWeeklyHours(location).map((row) => (
                      <span key={row.days}>
                        {row.days}: {row.hours}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Icon icon="LocationDot" className="mt-0.5 size-4 shrink-0" />
                  {location.mapUrl ? (
                    <Link
                      to={location.mapUrl}
                      reloadDocument
                      target="_blank"
                      rel="noreferrer"
                      className="text-on-surface underline underline-offset-2"
                    >
                      {formatAddress(location)}
                    </Link>
                  ) : (
                    <span>{formatAddress(location)}</span>
                  )}
                </div>
              </div>
            </Disclosure>

            {location.promotions.map((promotion) => (
              <div
                key={promotion.value}
                className="flex items-center gap-2 body-sm text-on-surface-variant"
              >
                <Icon icon="Tag" className="size-4 shrink-0" />
                {promotion.value}
              </div>
            ))}
          </li>
        ))}
      </Divide>

      <Link
        to={ALL_LOCATIONS_URL}
        reloadDocument
        target="_blank"
        rel="noreferrer"
        className="mt-4 text-on-surface underline underline-offset-2"
      >
        See all Happy Returns® locations
      </Link>
    </CardOption>
  );
}
